<div class="llama-maverick-dialog">
  <button
    class="close-btn"
    mat-icon-button
    (click)="close()"
    aria-label="Close Llama 4 Info"
  >
    ×
  </button>

  <div class="header">
    <img src="assets/logos/meta.svg" alt="Llama 4 Logo" class="mini-logo" />
    <h2>Llama 4</h2>
    <div class="sub">Meta’s open-weight frontier model built for multimodal reasoning, long context, and scalable deployment</div>
  </div>

  <div class="body">
    <p>
      <strong>Llama 4</strong> is a flagship, high-capability model in Meta’s Llama 4 lineup, designed to be a
      reliable “generalist” that performs well across <strong>reasoning</strong>, <strong>coding</strong>, and
      <strong>multimodal</strong> tasks. It’s often positioned for teams that want strong performance while keeping
      flexibility over <strong>deployment</strong>, <strong>customization</strong>, and <strong>cost</strong>.
    </p>

    <div class="optimal">
      <h3>What “open-weight” means in practice</h3>
      <ul>
        <li><strong>More control:</strong> You can host the model in your own environment, apply guardrails, and tune policies to your needs.</li>
        <li><strong>Customization paths:</strong> Easier to fine-tune, distill, or adapt for domain-specific workflows compared to closed models.</li>
        <li><strong>Deployment choice:</strong> Run on your infrastructure or via supported managed offerings, depending on your constraints.</li>
      </ul>
    </div>

    <ul>
      <li>🧠 <strong>Frontier-grade reasoning:</strong> Handles multi-step problems, constraint-heavy prompts, and structured decision workflows with better stability than “lightweight” models.</li>
      <li>👨‍💻 <strong>Production-oriented coding:</strong> Helpful for generation, refactoring, tests, debugging, and code review—especially when paired with strong prompting and tool use.</li>
      <li>🖼️ <strong>Multimodal understanding:</strong> Built to work with text + images for document interpretation, UI understanding, diagram reasoning, and visual Q&A.</li>
      <li>📚 <strong>Long-context workflows:</strong> Frequently deployed in configurations that support very large context windows, enabling analysis of long documents, large specs, and multi-file codebases.</li>
      <li>⚙️ <strong>MoE efficiency:</strong> A Mixture-of-Experts architecture typically activates only subsets of parameters per request, improving throughput and lowering compute cost versus dense models at similar capability.</li>
      <li>🌍 <strong>Multilingual strength:</strong> Strong performance across many languages and mixed-language content, useful for global teams and international support.</li>
      <li>🏢 <strong>Enterprise readiness:</strong> Commonly used for scalable assistants, internal copilots, and customer-facing automation where predictable performance matters.</li>
    </ul>

    <div class="optimal">
      <h3>Best uses for Llama 4</h3>
      <ul>
        <li><strong>Enterprise copilots:</strong> knowledge base Q&A, internal tooling, SOP guidance, and ticket triage</li>
        <li><strong>Engineering workflows:</strong> code review helpers, migration assistants, CI explanations, and runbook automation</li>
        <li><strong>Long-document analysis:</strong> requirements, legal docs, policy packs, research summaries, and technical specs</li>
        <li><strong>Multimodal apps:</strong> extracting meaning from screenshots, forms, diagrams, and slide decks</li>
        <li><strong>Custom model programs:</strong> fine-tuning, RAG, eval-driven iteration, and domain adaptation</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>How to get the best results</h3>
      <ul>
        <li><strong>Be explicit:</strong> give the model a role, goal, constraints, and output format (bullets, JSON, table, etc.).</li>
        <li><strong>Use grounding:</strong> provide source text or connect retrieval (RAG) so answers are tied to your documents.</li>
        <li><strong>Ask for checks:</strong> request assumptions, edge cases, and a short validation step for critical outputs.</li>
        <li><strong>Chunk long inputs:</strong> for huge documents, structure sections and add “what to do” instructions per section.</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Where it fits compared to other model types</h3>
      <ul>
        <li><strong>Versus small models:</strong> better reasoning + robustness, especially on messy real-world tasks and long instructions.</li>
        <li><strong>Versus closed frontier models:</strong> typically more control and deployment flexibility, often at the cost of “absolute best” peak capability.</li>
        <li><strong>Versus dense open models:</strong> MoE designs can offer better efficiency at scale, depending on serving stack and hardware.</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Pro tip</h3>
      <ul>
        <li>
          If you’re building a real product, pair Maverick with <strong>evaluation</strong> (golden sets + regression tests),
          <strong>retrieval</strong> (RAG), and <strong>guardrails</strong>. This is what turns a strong model into a dependable system.
        </li>
      </ul>
    </div>
  </div>
</div>
